import { ENTRY_RECIPE_ID } from '../redux/mealEntry';

/**
 * The main dish of each slot (#746).
 *
 * A slot can hold a main and its accompaniments. Rules that reason about "the
 * dinner of a day" — no third chicken in a week, no tacos twice in a fortnight
 * — are about the main: a green salad beside Tuesday's roast is not Tuesday's
 * dinner, and counting it as one made a side simmered in chicken stock the
 * week's third chicken.
 *
 * The main is the slot's entry when it holds one — a note (« Porcs Sevelin
 * BBQ ») or leftovers with a salad beside them — otherwise its first accepted
 * dish, oldest first. The same dish the planner draws as the slot's card
 * (`slotMainOf`).
 */
interface SlotMeal {
  scheduledFor: string;
  mealType: string;
  mode?: string;
  createdAt?: string;
  recipeId?: string;
  entry?: unknown;
}

export function primaryMealsOf<T extends SlotMeal>(meals: readonly T[]): T[] {
  const primaries = new Map<string, T>();
  for (const meal of meals) {
    if (meal.mode === 'proposed') continue;
    const key = `${meal.scheduledFor}|${meal.mealType}`;
    const current = primaries.get(key);
    if (!current || comesFirst(meal, current)) primaries.set(key, meal);
  }
  const kept = new Set(primaries.values());
  return meals.filter((meal) => kept.has(meal));
}

const isEntry = (m: SlotMeal) => m.recipeId === ENTRY_RECIPE_ID && !!m.entry;

function comesFirst(a: SlotMeal, b: SlotMeal): boolean {
  if (isEntry(a) !== isEntry(b)) return isEntry(a);
  return isOlder(a, b);
}

/** Meals without a stamp keep their order, after the stamped ones. */
function isOlder(a: SlotMeal, b: SlotMeal): boolean {
  if (!a.createdAt) return false;
  if (!b.createdAt) return true;
  return a.createdAt < b.createdAt;
}
